'use client';

import { c as _c } from "react/compiler-runtime";
import { cn } from '@repo/ui/lib/utils';
import { ChevronDown, Search } from 'lucide-react';
import type { RefObject } from 'react';
import type { BundleSortOption, BundleTrade } from './bundle-types';
interface BundleSearchControlsProps {
  query: string;
  onQueryChange: (value: string) => void;
  searchInputRef: RefObject<HTMLInputElement | null>;
  trades: BundleTrade[];
  activeTrade: string | null;
  onTradeChange: (slug: string | null) => void;
  sortValue: string;
  onSortChange: (value: string) => void;
  sortOptions: BundleSortOption[];
  showTradePills: boolean;
  showKeyboardHint: boolean;
  keyboardHintText: string;
}
export function BundleSearchControls(t0) {
  const $ = _c(26);
  const {
    query,
    onQueryChange,
    searchInputRef,
    trades,
    activeTrade,
    onTradeChange,
    sortValue,
    onSortChange,
    sortOptions,
    showTradePills,
    showKeyboardHint,
    keyboardHintText
  } = t0;
  const hasTrades = showTradePills && trades.length > 0;
  const hasSort = sortOptions.length > 0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== onQueryChange) {
    t2 = event => onQueryChange(event.target.value);
    $[1] = onQueryChange;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== query || $[4] !== searchInputRef || $[5] !== t2) {
    t3 = <input ref={searchInputRef} type="text" value={query} onChange={t2} placeholder="Search by design, trade, or keyword..." aria-label="Search products" className="h-12 w-full rounded-lg border border-neutral-200 bg-neutral-50 pl-11 pr-16 text-sm text-neutral-900 transition-colors placeholder:text-neutral-400 focus:border-neutral-900 focus:bg-white focus:outline-none focus:ring-1 focus:ring-neutral-900" />;
    $[3] = query;
    $[4] = searchInputRef;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== keyboardHintText || $[8] !== query || $[9] !== showKeyboardHint) {
    t4 = showKeyboardHint && !query && <span className="pointer-events-none absolute right-3 top-1/2 hidden -translate-y-1/2 rounded border border-neutral-200 bg-white px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-neutral-400 md:inline-block">{keyboardHintText}</span>;
    $[7] = keyboardHintText;
    $[8] = query;
    $[9] = showKeyboardHint;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== t3 || $[12] !== t4) {
    t5 = <div className="relative">{t1}{t3}{t4}</div>;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== activeTrade || $[15] !== hasSort || $[16] !== hasTrades || $[17] !== onSortChange || $[18] !== onTradeChange || $[19] !== sortOptions || $[20] !== sortValue || $[21] !== trades) {
    t6 = (hasTrades || hasSort) && <div className="flex items-center gap-3">{hasTrades && <div className="relative min-w-0 flex-1"><div className="flex gap-2 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"><TradePill active={activeTrade === null} onClick={() => onTradeChange(null)}>All Trades</TradePill>{trades.map(trade => <TradePill key={trade.slug} active={activeTrade === trade.slug} onClick={() => onTradeChange(activeTrade === trade.slug ? null : trade.slug)}>{trade.name}</TradePill>)}</div><div className="pointer-events-none absolute inset-y-0 right-0 w-10 bg-gradient-to-l from-white to-transparent" /></div>}{hasSort && <div className="relative shrink-0"><select value={sortValue} onChange={event_0 => onSortChange(event_0.target.value)} aria-label="Sort products" className="h-9 cursor-pointer appearance-none rounded-full border border-neutral-200 bg-white pl-4 pr-9 text-sm font-medium text-neutral-700 transition-colors hover:border-neutral-400 focus:border-neutral-900 focus:outline-none">{sortOptions.map(_temp)}</select><ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" /></div>}</div>;
    $[14] = activeTrade;
    $[15] = hasSort;
    $[16] = hasTrades;
    $[17] = onSortChange;
    $[18] = onTradeChange;
    $[19] = sortOptions;
    $[20] = sortValue;
    $[21] = trades;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  let t7;
  if ($[23] !== t5 || $[24] !== t6) {
    t7 = <div className="space-y-3">{t5}{t6}</div>;
    $[23] = t5;
    $[24] = t6;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  return t7;
}
function _temp(option) {
  return <option key={option.value} value={option.value}>{option.label}</option>;
}
function TradePill(t0) {
  const $ = _c(6);
  const {
    active,
    onClick,
    children
  } = t0;
  const t1 = active ? "border-neutral-900 bg-neutral-900 text-white" : "border-neutral-200 bg-white text-neutral-600 hover:border-neutral-400 hover:text-neutral-900";
  let t2;
  if ($[0] !== t1) {
    t2 = cn("h-9 shrink-0 whitespace-nowrap rounded-full border px-4 text-sm font-medium transition-colors", t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== children || $[3] !== onClick || $[4] !== t2) {
    t3 = <button type="button" onClick={onClick} className={t2}>{children}</button>;
    $[2] = children;
    $[3] = onClick;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
}